import { formatServerTime, useServerTime } from '@adiox/ui';
import { useStream } from '../../stream/stream';
import { useActiveLogin } from '../../trading/useTrading';
import { useChartCommands } from '../../chart/chartCommands';

const CONNECTION = {
  connected: 'Connected',
  connecting: 'Connecting…',
  reconnecting: 'Reconnecting…',
  disconnected: 'No connection',
} as const;

/** Signal bars: green while connected and prices flow, amber while connecting, red without a connection. */
function Signal({ tone }: { tone: 'ok' | 'wait' | 'down' }) {
  const color = tone === 'ok' ? '#1a9b35' : tone === 'wait' ? '#d08a00' : '#d4261f';
  return (
    <svg width="14" height="12" viewBox="0 0 14 12" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <rect
          key={i}
          x={i * 3.5}
          y={9 - i * 3}
          width="2.5"
          height={3 + i * 3}
          fill={tone === 'down' && i > 0 ? '#bbb' : color}
        />
      ))}
    </svg>
  );
}

/**
 * The classic status bar: help hint, the profile, the active chart, server time, account and the connection (with the
 * price feed's state).
 */
export function StatusBar({ profile }: { profile: string }) {
  const hubState = useStream((s) => s.hubState);
  const feedUp = useStream((s) => s.feedUp);
  const login = useActiveLogin();
  const time = useServerTime();
  const chart = useChartCommands((s) => s.active?.state);
  const tone =
    hubState !== 'connected'
      ? hubState === 'disconnected'
        ? 'down'
        : 'wait'
      : feedUp === false
        ? 'wait'
        : 'ok';
  const connection =
    hubState === 'connected' && feedUp === false ? 'Connected · prices paused' : CONNECTION[hubState];

  return (
    <footer className="classic-statusbar flex shrink-0 items-stretch" data-testid="status-bar">
      <span className="flex-1 truncate">For Help, press F1</span>
      <span className="w-32 truncate">{profile}</span>
      <span className="w-28 truncate">{chart ? `${chart.symbol},${chart.tf}` : ''}</span>
      <span className="num w-20">{time ? formatServerTime(time) : '--:--:--'}</span>
      <span className="w-24 truncate">{login === undefined ? '' : `#${login}`}</span>
      <span
        className="flex w-48 items-center gap-1.5"
        role="status"
        aria-live="polite"
        data-testid="connection-status"
      >
        <Signal tone={tone} />
        {connection}
      </span>
    </footer>
  );
}
